import { useEffect, useState } from "react";

import { getPhoneRuleConfigs } from "../api/services/phoneRuleConfigs";
import { IPhoneRuleConfig } from "../api/services/phoneRuleConfigs/models";
import { SA_FALLBACK_CONFIG } from "./phoneRuleConfig.helpers";

interface UsePhoneRuleConfigsResult {
  countries: IPhoneRuleConfig[];
  isLoading: boolean;
}

// The list rarely changes and the party modal mounts once per step, so share one request
// across every caller for the lifetime of the page. A failed request is not cached.
let configsRequest: Promise<IPhoneRuleConfig[]> | null = null;

const loadEnabledCountries = (): Promise<IPhoneRuleConfig[]> => {
  if (!configsRequest) {
    configsRequest = getPhoneRuleConfigs()
      .then((response) =>
        (response?.data?.configs ?? []).filter((country) => country.enabled)
      )
      .catch((error) => {
        configsRequest = null;
        throw error;
      });
  }
  return configsRequest;
};

/**
 * Loads the supported-countries phone rule configs.
 *
 * While the request is in flight — and if it ultimately fails — Saudi Arabia is the only
 * option, which is exactly the pre-existing behaviour. Only enabled countries are exposed,
 * preserving the order returned by the backend.
 */
export const usePhoneRuleConfigs = (): UsePhoneRuleConfigsResult => {
  const [countries, setCountries] = useState<IPhoneRuleConfig[]>([
    SA_FALLBACK_CONFIG,
  ]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isActive = true;

    loadEnabledCountries()
      .then((enabledCountries) => {
        if (isActive && enabledCountries.length) {
          setCountries(enabledCountries);
        }
      })
      .catch((error) => {
        console.error("Failed to load phone rule configs:", error);
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, []);

  return { countries, isLoading };
};
